import { SuggestionSearchBarOptionBase } from '@aibees/elephant/feature/suggestion-search-bar';
import { Icon, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Stack } from '@mui/material';
import { cloneElement, useRef } from 'react';
import { useSuggestionSearchBarContext } from '../../context/context';
import { LookalikeButton } from '../lookalike-button';
import * as Styled from './styles';
import * as SX from './sx';
import type { SingleItemProps as Props } from './types';

export const SingleItem = <T extends SuggestionSearchBarOptionBase>({
  className,
  item,
}: Props<T>) => {
  const { id, label, value, description, group, logo } = item;

  const {
    onHighlightItem,
    highlightedItem,
    detailActionElement,
    searchCategory,
  } = useSuggestionSearchBarContext();

  const handleSelect = () => {
    onHighlightItem(item);
  };

  const divRef = useRef<HTMLDivElement | null>(null);

  const clonedAction =
    highlightedItem &&
    detailActionElement &&
    cloneElement(detailActionElement?.(item), {
      customProp: { id }, // Add customProp to the cloned element
    });

  const getAvatarIcon = () => {
    if (searchCategory === 'people') {
      return <Icon size={20} name="person-outline" color="grey.400" />;
    } else if (searchCategory === 'company') {
      return <Icon size={20} name="business" color="grey.400" />;
    }
    return <Icon size={20} name="person-outline" color="grey.400" />;
  };

  return (
    <Styled.Wrapper
      className={classnames('SingleItem', className)}
      sx={SX.root(highlightedItem?.id === id)}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      onClick={handleSelect}
      ref={divRef}
    >
      <Avatar src={logo} variant="rounded" size={40} color="white">
        {getAvatarIcon()}
      </Avatar>

      <Stack
        sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}
      >
        <TextEllipsis
          variant="body2"
          fontWeight={700}
          value={label}
          color="grey.700"
        />
        <TextEllipsis
          variant="body4"
          color="grey.500"
          fontWeight={500}
          value={description}
        />
      </Stack>
      {detailActionElement ? (
        clonedAction
      ) : (
        <LookalikeButton sx={{ flexShrink: 0 }} item={item} />
      )}
    </Styled.Wrapper>
  );
};

SingleItem.displayName = 'SingleItem';
